import React from 'react';
import { Heart, Code2, Github, Linkedin, Twitter, Mail, Terminal, ArrowUpRight, BookOpen } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { icon: Linkedin, href: 'https://www.linkedin.com/in/tushar-singh-1ba975296/', label: 'LinkedIn' },
    { icon: Github, href: 'https://github.com/tushar80rt', label: 'GitHub' },
    { icon: Twitter, href: 'https://x.com/_tusharrathore', label: 'Twitter' },
    { icon: BookOpen, href: 'https://dev.to/_tusharrathore', label: 'Blog' },
    { icon: Mail, href: 'mailto:tushar80rt@gmail.com', label: 'Email' }
  ];

  const quickLinks = ['About', 'Skills', 'Experience', 'Projects', 'Contact'];

  return (
    <footer className="relative bg-[var(--bg-primary)] border-t border-[var(--border-subtle)] pt-20 pb-10 overflow-hidden">
      {/* Background Glow */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-[var(--brand-primary)]/5 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid md:grid-cols-4 gap-12 mb-16">
          {/* Brand Column (Spans 2 columns on desktop) */}
          <div className="md:col-span-2 space-y-6">
            <a href="#hero" className="flex items-center gap-2 group w-fit">
              <div className="p-2 rounded-lg bg-white/5 border border-white/10 group-hover:border-[var(--brand-primary)] transition-colors duration-300">
                <Terminal size={24} className="text-[var(--brand-primary)]" />
              </div>
              <span className="text-2xl font-bold tracking-tight text-[var(--text-primary)]">
                Tushar<span className="text-[var(--brand-primary)]">.dev</span>
              </span>
            </a>
            <p className="text-[var(--text-secondary)] leading-relaxed max-w-md text-sm md:text-base">
              Data Scientist focused on machine learning, RAG systems, and multi-agent workflows.
              Building practical AI products that deliver measurable real-world impact.
            </p>
            <div className="flex gap-4">
              {socialLinks.map((social, index) => {
                const Icon = social.icon;
                return (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 rounded-full bg-[var(--bg-overlay)] border border-[var(--border-subtle)] hover:bg-[var(--brand-primary)] hover:text-black hover:border-[var(--brand-primary)] transition-all duration-300 group"
                    aria-label={social.label}
                  >
                    <Icon size={18} className="group-hover:scale-110 transition-transform duration-300" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold text-white mb-6">Navigation</h4>
            <ul className="space-y-4">
              {quickLinks.map((link) => (
                <li key={link}>
                  <a
                    href={`#${link.toLowerCase()}`}
                    className="text-gray-400 hover:text-[var(--brand-primary)] flex items-center gap-2 group transition-colors duration-300 text-sm"
                  >
                    <span className="w-0 h-[1px] bg-[var(--brand-primary)] group-hover:w-3 transition-all duration-300" />
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact / Info */}
          <div>
            <h4 className="text-lg font-bold text-[var(--text-primary)] mb-6">Get in Touch</h4>
            <div className="space-y-4">
              <p className="text-[var(--text-secondary)] text-sm">
                Open for freelance projects and collaborations.
              </p>
              <a 
                href="mailto:tushar80rt@gmail.com"
                className="inline-flex items-center gap-2 text-[var(--brand-primary)] hover:text-[var(--text-primary)] transition-colors duration-300 font-medium"
              >
                tushar80rt@gmail.com
                <ArrowUpRight size={16} />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-[var(--border-subtle)] flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[var(--text-muted)] text-sm">
            © {currentYear} Tushar Singh. All rights reserved.
          </p>
          
          <div className="flex items-center gap-2 text-sm text-[var(--text-muted)] bg-[var(--bg-overlay)] px-4 py-2 rounded-full border border-[var(--border-subtle)]">
            <Code2 size={14} className="text-[var(--brand-primary)]" />
            <span>with</span>
            <Heart size={14} className="text-red-500 animate-pulse" fill="currentColor" />
            <span>Tushar Singh</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;